import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { useDebounce } from "../hooks/useDebounce";
import { useAliasValidation } from "../hooks/useAliasValidation";
import InputField from "../components/input-field/InputField";
import { ErrorBanner } from "./ErrorBanner";
import ClickableText from "../components/base/ClickableText";
import Button from "../components/base/Button";
import { CheckmarkIcon } from "../components/Icons/Icons";
import Spinner from "../components/base/Spinner";
import Modal from "../components/modal/Modal";
import { useLocalizedString } from "@amzn/aws-marketing-identity-kit-localizations";
import { Sanitize } from "../components/Sanitize";
import { hasError } from "../utils/utils";
import { APIErrorCodes } from "../constants/profile";
export const ClaimAlias = ({ adapter, onAliasSet, profile, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [alias, setAlias] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayBanner, setDisplayBanner] = useState(false);
    const debouncedAlias = useDebounce(alias, 500);
    const { t } = useLocalizedString();
    const { validation, isChecking, checkAvailability } = useAliasValidation(alias, adapter);
    useEffect(() => {
        if (displayBanner) {
            setDisplayBanner(false);
        }
    }, [alias]);
    useEffect(() => {
        if (debouncedAlias) {
            checkAvailability(debouncedAlias);
        }
    }, [debouncedAlias, checkAvailability]);
    const handleAliasChange = (e) => {
        setAlias(e.target.value.toLowerCase());
    };
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await adapter.SetAlias(alias.toLowerCase(), profile?.userId);
            setIsOpen(false);
            onAliasSet(alias);
        }
        catch (error) {
            if (error && hasError(error, APIErrorCodes.AliasalreadyAssigned)) {
                window.location.reload();
            }
            else {
                setDisplayBanner(true);
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleClose = () => {
        setIsOpen(false);
        setAlias("");
        setDisplayBanner(false);
    };
    const isButtonDisabled = !validation.isValid || alias.length === 0 || isSubmitting || isChecking;
    const showCancelButton = !isSubmitting && validation.isValid && !isChecking;
    const showConfirmation = validation.isValid && !displayBanner && !isChecking;
    return (_jsxs(_Fragment, { children: [_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: "flex flex-col gap-3", children: [_jsx("h2", { className: "text-4xl font-bold", children: t("Claim your alias") }), _jsx("p", { className: "mt-16", children: t("Your alias will be visible to all members of our community and is how other builders connect with you. Your alias cannot be changed after claiming.") }), _jsx(InputField, { className: "my-4", label: t("Alias"), name: "Alias", value: alias, onChange: handleAliasChange, "aria-label": "Enter your alias", error: !validation.length.isValid
                                ? validation.length.message
                                : !validation.alphanumeric.isValid
                                    ? validation.alphanumeric.message
                                    : !validation.availability.isValid
                                        ? validation.availability.message
                                        : "", disabled: isSubmitting }), displayBanner && _jsx(ErrorBanner, {}), _jsx("div", { className: "text-sm text-gray-500", children: _jsx(Sanitize, { html: t('Your alias must be 3 to 19 alphanumeric characters (contains only a-z, 0-9) and not violate our <a class="link" href="https://aws.amazon.com/codeofconduct/" rel="noopener noreferrer" target="_blank">Terms and Services</a>.') }) }), _jsxs("div", { className: "mt-4 flex items-center justify-between", children: [_jsx("div", { className: "flex-grow", children: showConfirmation && (_jsx("span", { className: "text-[14px] text-red-600", children: t("Are you sure you want to claim? This action cannot be undone.") })) }), _jsxs("div", { className: "flex gap-6 ml-1", children: [showCancelButton && (_jsx(ClickableText, { className: "h-min my-auto", onClick: handleClose, children: t("Cancel") })), _jsx(Button, { onClick: handleSubmit, disabled: isButtonDisabled, children: _jsxs(_Fragment, { children: [t(isSubmitting
                                                        ? "Claiming"
                                                        : isChecking
                                                            ? "Checking availability"
                                                            : "Claim"), validation.isValid && !isSubmitting && !isChecking && (_jsx(CheckmarkIcon, {})), (isSubmitting || isChecking) && _jsx(Spinner, {})] }) })] })] })] }) }), _jsx(Button, { onClick: () => setIsOpen(true), children: t("Claim") })] }));
};
export default ClaimAlias;
//# sourceMappingURL=ClaimAlias.js.map